import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

// Properties are listed explicitly instead of `transition-all`: transitioning `opacity` triggers a
// paint-invalidation bug in Safari's compositor that leaves a ghost of the button behind.
const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors transition-shadow transition-transform disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-accent focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer disabled:cursor-not-allowed dark:aria-invalid:border-destructive",
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/80',
                danger: 'bg-destructive text-white shadow-xs hover:bg-destructive/80 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 hover:dark:bg-destructive/48',
                destructive:
                    'bg-destructive-foreground text-destructive shadow-none hover:bg-destructive/15 focus-visible:ring-destructive-foreground/8 dark:bg-destructive-foreground focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
                outline:
                    'border shadow-none hover:bg-accent/30 hover:text-accent-foreground dark:border-input dark:hover:bg-accent/30',
                secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
                ghost: 'text-foreground hover:text-accent-foreground hover:bg-accent/50',
                link: 'text-foreground underline-offset-4 hover:opacity-80 active:opacity-60',
                'inline-link':
                    'text-inherit underline underline-offset-4 hover:opacity-80 active:opacity-60',
                inline: 'justify-start whitespace-normal text-left text-base font-normal text-muted-foreground hover:opacity-80 active:opacity-60 data-[state=open]:bg-accent/50',
                'inline-copy':
                    'relative justify-start gap-1 whitespace-normal text-left text-base font-normal text-muted-foreground hover:bg-accent/50 data-copied:bg-accent/50 active:opacity-60',
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                sm: 'rounded-md gap-1.5 px-2.5 py-1',
                lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
                icon: 'size-9',
                'icon-sm': 'size-7',
                'icon-lg': 'size-10',
                inline: 'h-auto rounded-sm px-0 py-0.5',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
        compoundVariants: [
            // Sits inside a sentence, so it drops the box every size gives a standalone button.
            {
                variant: 'inline-link',
                className: 'h-auto rounded-sm p-0 align-baseline font-normal has-[>svg]:px-0',
            },
        ],
    },
);

const Button = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> &
        VariantProps<typeof buttonVariants> & {
            asChild?: boolean;
        }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            data-slot="button"
            tabIndex={0} // required for tabulation on new safari versions to work
            className={cn(buttonVariants({ variant, size, className }))}
            ref={ref}
            {...props}
        />
    );
});
Button.displayName = 'Button';

export { Button, buttonVariants };
